import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Brain,
  Users,
  ListOrdered,
  MessageCircleQuestion,
  Flame,
  Bell,
  Settings,
  Search,
  TrendingUp,
} from "lucide-react";
import { getDatabase, ref, onValue, off } from "firebase/database";
import BetVip from "./BetVip";
import SocialWorld from "../World/SocialWorld";
import Marcadores from "../Home/Marcadores";
import ChatIA from "./ChatIA";
import Combi from "./Combi";
import { BsTelephone, BsWhatsapp } from "react-icons/bs";
import { FaBlog } from "react-icons/fa";
// Importamos el componente de configuración
import ConfigModal from "./ConfigModal";

// Componente Banner que lee datos desde Firebase
const BannerTendencias = () => {
  const [noticias, setNoticias] = useState([]);
  const [isAnimating, setIsAnimating] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getDatabase();
    const bannerRef = ref(db, "App/BannerVip");

    // Suscribirse a los cambios en tiempo real
    onValue(
      bannerRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setNoticias(data);
        } else {
          console.log("No hay datos disponibles en el banner");
          // Datos por defecto en caso de que no haya nada en Firebase
          setNoticias([
            {
              texto: "🔥 Real Madrid vs Barcelona - Cuota especial 2.5",
              url: "/eventos/clasico",
            },
            {
              texto: "⚽ Premier League - Liverpool lidera la tabla",
              url: "/ligas/premier",
            },
          ]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error al leer los datos del banner:", error);
        setLoading(false);
      }
    );

    // Limpiar la suscripción al desmontar
    return () => {
      off(bannerRef);
    };
  }, []);

  // Pausar animación al pasar el cursor o tocar en móviles
  const handleMouseEnter = () => setIsAnimating(false);
  const handleMouseLeave = () => setIsAnimating(true);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-3 w-full">
        <div className="flex items-center justify-center">
          <div className="animate-pulse h-4 bg-blue-700 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-3 w-full overflow-hidden shadow-lg">
      <div
        className="flex items-center space-x-2"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
      >
        <div className="flex-shrink-0">
          <TrendingUp size={18} className="text-yellow-400 animate-pulse" />
        </div>

        <div
          className={`whitespace-nowrap ${
            isAnimating ? "animate-marquee" : ""
          } overflow-x-auto scrollbar-hide`}
        >
          {noticias.map((noticia, index) => (
            <a
              key={index}
              href={noticia.url}
              className="inline-block mx-4 text-white hover:text-yellow-300 transition-colors duration-300"
            >
              <span className="md:text-base text-sm font-medium">
                {noticia.texto}
              </span>
            </a>
          ))}

          {/* Repetir noticias para dar efecto de desplazamiento infinito */}
          {noticias.map((noticia, index) => (
            <a
              key={`repeat-${index}`}
              href={noticia.url}
              className="inline-block mx-4 text-white hover:text-yellow-300 transition-colors duration-300"
            >
              <span className="md:text-base text-sm font-medium">
                {noticia.texto}
              </span>
            </a>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-marquee {
          animation: marquee 20s linear infinite;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const tabs = [
  { id: "Inicio", label: "Inicio", icon: Home, color: "text-blue-500" },
  { id: "Feed", label: "Feed", icon: Users, color: "text-green-500" },
  { id: "Parlay", label: "Parlay", icon: Flame, color: "text-red-500" },
  {
    id: "Marcador",
    label: "Marcador",
    icon: ListOrdered,
    color: "text-yellow-500",
  },
  { id: "IA", label: "Asistente", icon: Brain, color: "text-purple-500" },
];

export default function HomeVip() {
  const [activeTab, setActiveTab] = useState("Inicio");
  const [showNotification, setShowNotification] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Estado para controlar la visibilidad del modal de configuración
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  // Detecta cuando la pestaña es "WhatsApp" y redirige
  useEffect(() => {
    if (activeTab === "WhatsApp") {
      window.location.href = "https://wa.me/573023580862?text=Hey!%20Pro";
    }
  }, [activeTab]);

  // Función para manejar el clic en el icono flotante de Flame
  const handleFlameClick = () => {
    setActiveTab("Parlay");
  };

  // Renderizado condicional basado en la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case "Inicio":
        return <BetVip />;
      case "Feed":
        return <SocialWorld />;
      case "Marcador":
        return <Marcadores />;
      case "Parlay":
        return <Combi />;
      case "IA":
        return <ChatIA />;
      default:
        return <BetVip />;
    }
  };

  // Manejar búsqueda
  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchQuery("");
    }
  };

  // Manejar notificaciones
  const handleNotificationClick = () => {
    window.location.href = "https://wa.me/573023580862?text=Hey!%20Pro";
  };

  const handleBlogLink = () => {
    window.location.href = "https://deportivosblogs.online/";
  };

  const handleTel = () => {
    window.location.href = "tel:3023580862";
  };

  // Función para abrir modal de configuración
  const handleSettingsClick = () => {
    setIsConfigModalOpen(true);
  };

  // Función para cerrar modal de configuración
  const handleCloseConfigModal = () => {
    setIsConfigModalOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col w-full bg-gradient-to-b from-gray-900 to-blue-900 text-white">
      {/* Banner de tendencias desde Firebase */}
      <BannerTendencias />

      {/* Cabecera con búsqueda y notificaciones */}
      <header className="p-3 flex justify-between items-center">
        <div className="text-xl font-bold">VIP🔥</div>

        <div className="flex items-center space-x-3">
          <>
            <div className="relative">
              <button onClick={handleNotificationClick}>
                <BsWhatsapp size={20} className="text-gray-300" />
              </button>
            </div>
            <button onClick={handleTel}>
              <BsTelephone size={20} className="text-gray-300" />
            </button>
            <button onClick={handleBlogLink}>
              <FaBlog size={20} className="text-gray-300" />
            </button>
            <button onClick={handleSettingsClick}>
              <Settings size={20} className="text-gray-300" />
            </button>
          </>
        </div>
      </header>

      {/* Contenedor principal con animación */}
      <AnimatePresence mode="wait">
        <motion.main
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="flex-grow w-full p-3 mb-16" // Añadido margen inferior para evitar que el contenido quede detrás de la barra de navegación
        >
          {renderTabContent()}
        </motion.main>
      </AnimatePresence>

      {/* Barra de navegación fija en la parte inferior */}
      <nav className="fixed bottom-0 left-0 w-full bg-gradient-to-r from-blue-900 to-blue-700 shadow-lg rounded-t-xl z-10">
        <div className="flex justify-around p-2">
          {tabs.map(({ id, label, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="relative flex flex-col items-center text-white transition-all duration-300 hover:opacity-75 py-1"
            >
              <div
                className={`p-2 rounded-full ${
                  activeTab === id ? `bg-opacity-20 bg-white ${color}` : ""
                }`}
              >
                <Icon
                  size={20}
                  className={activeTab === id ? color : "text-gray-300"}
                />
              </div>
              <span
                className={`text-xs mt-1 ${
                  activeTab === id ? "font-medium " + color : "text-gray-300"
                }`}
              >
                {label}
              </span>
              {activeTab === id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -bottom-2 h-1 w-8 bg-white rounded-full"
                />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Animación flotante para promociones - Ahora abre Combi al hacer clic */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: [0, -10, 0],
          transition: { y: { repeat: Infinity, duration: 2 } },
        }}
        onClick={handleFlameClick}
        className="fixed bottom-20 right-4 bg-gradient-to-r from-yellow-500 to-red-500 rounded-full p-3 shadow-lg cursor-pointer z-10"
      >
        <Flame size={24} className="text-white" />
      </motion.div>

      {/* Modal de configuración */}
      <ConfigModal
        isOpen={isConfigModalOpen}
        onClose={handleCloseConfigModal}
      />
    </div>
  );
}
